"use client";

import { useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Edgeconfig, EventData } from "@types";
import { EventMode } from "@utils/enums";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { EventListMode } from "@features/eventListMode";
import EventCalendarMode from "@features/eventCalendarMode";
import EventInfo from "@features/eventPage/components/eventInfo";
import EventInfoAccordion from "@features/eventPage/components/eventInfoAccordion";
import calculatePriceRange from "@features/eventPage/utils/calculatePriceRange";
import EventTicketButton from "@features/eventPage/components/eventTicketButton";
import EventPastError from "@features/eventPage/components/eventPastError";
import EventDisabledError from "@features/eventPage/components/eventDisabledError";
import EventSubDescription from "@features/eventPage/components/eventSubDescription";
import EventDescription from "@features/eventPage/components/eventDescription";
import LandingPageLink from "../components/landingPageLink";
import { Heading } from "@components/theming";

interface Props {
  event: EventData;
  edgeConfig: Edgeconfig | undefined;
}

export default function EventDetails({ event, edgeConfig }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const viewMode = searchParams.get("view");
  const promo = searchParams.get("promo");
  const t = useTranslations("EventPage");
  const locale = useLocale();

  const showEventDescription = viewMode?.includes("info");

  const getPriceRange = useCallback(
    (ids?: { pricesectionIds?: number[]; groupedPricesectionIds?: number[] }) =>
      calculatePriceRange(event.pricing.priceschemes ?? [], ids),
    [event.pricing.priceschemes]
  );

  const priceRange = getPriceRange();

  const priceRangeText =
    event.info.pricerange ||
    `${
      priceRange.min !== priceRange.max
        ? formatPrice(locale, event.currency.code, priceRange.min) + " - "
        : ""
    }${formatPrice(locale, event.currency.code, priceRange.max)} + ${t(
      "bookingfee"
    )}`;

  const description = event.description.replaceAll(
    `href="https://www.iticket.co.nz`,
    `href="`
  );

  return (
    <div id="event_details_container" className="mt-4">
      <div className="flex flex-col md:flex-row items-center gap-1 md:gap-4">
        {event.status === "past" ? (
          <EventPastError />
        ) : edgeConfig?.eventPageSales.disabledByOrganisation.includes(
            event.organisation.uid
          ) ? (
          <EventDisabledError />
        ) : (
          <EventTicketButton
            showEventDescription={showEventDescription}
            href={
              showEventDescription
                ? `${pathname}${promo ? `?promo=${promo}` : ""}`
                : `${pathname}?${promo ? `promo=${promo}&` : ""}view=info`
            }
            priceText={priceRangeText}
          />
        )}
      </div>
      {event.subdescription ? (
        <EventSubDescription subdescription={event.subdescription} />
      ) : (
        <></>
      )}
      <div
        className={joinClassNames(
          "lg:hidden",
          event.subdescription ? "mt-4" : ""
        )}
      >
        <EventInfo event={event} />
      </div>
      {!showEventDescription &&
      !edgeConfig?.eventPageSales.disabledByOrganisation.includes(
        event.organisation.uid
      ) &&
      event.status !== "past" ? (
        <AnimatePresence>
          {event.settings.showingdisplaytype === EventMode.LIST ? (
            <EventListMode event={event} getPriceRange={getPriceRange} />
          ) : (
            <EventCalendarMode event={event} getPriceRange={getPriceRange} />
          )}
        </AnimatePresence>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <EventInfoAccordion
            hasPaymentInfo={event.payment.length > 0}
            priceschemes={event.pricing.priceschemes}
            discount={event.discount}
            currency={event.currency.code}
          />
          <EventDescription description={description} />
        </motion.div>
      )}
      {event.landingpages && event.landingpages.length > 0 ? (
        <div className="flex flex-col gap-4 mt-8 max-sm:mt-8">
          <Heading sharedClasses="text-lg md:text-xl font-bold">
            {t("exploremore", {
              landingpages: event.landingpages
                .map((v, i) => {
                  return event.landingpages.length === 1
                    ? v.title
                    : event.landingpages.length === i + 2
                    ? `${v.title} & `
                    : event.landingpages.length > i + 1
                    ? `${v.title}, `
                    : v.title;
                })
                .join(""),
            })}
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {event.landingpages.map((landingpage) => (
              <LandingPageLink
                key={landingpage.id}
                slug={landingpage.url}
                image={landingpage.images.header}
                name={landingpage.title}
                showNameOnMobile={event.landingpages.length > 1}
              />
            ))}
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
